import React, { Suspense, useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import { Box, Loader, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ActionType, HeaderProps, ModalVariant } from '@/components/Modals/types';

import { isWorkspaceSettingsEmpty } from '../../utils';
import AddWorkspaceSettings from '../AddWorkspacesSettings';

interface AddWorkspaceSettingsModalProps {
  selectedWorkspaces?: string[] | null;
  onSubmit: (selectedWorkspaces: string[] | null | undefined) => Promise<boolean | undefined>;
  onClose: () => void;
  contentName: string;
  action?: Pick<ActionType, 'text'>;
  header?: Pick<HeaderProps, 'title' | 'description'>;
  disabled?: boolean;
}

function AddWorkspaceSettingsModal(props: AddWorkspaceSettingsModalProps) {
  const { selectedWorkspaces, onClose, onSubmit, contentName, action, header, disabled } = props;
  const { t } = useTranslation('Workspace');

  const [workspaceState, setWorkspaceState] = useState(selectedWorkspaces);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const validate = useCallback(
    (val: string[] | null | undefined) => {
      const workspaceSettingsEmpty = isWorkspaceSettingsEmpty(true, val);
      if (workspaceSettingsEmpty) {
        setError(t('Workspace:WorkspaceSettingsEmpty'));
        return false;
      }
      setError('');
      return true;
    },
    [t],
  );

  const onWorkspaceChange = useCallback(
    (val: string[]) => {
      setWorkspaceState(val);
      error && validate(val);
    },
    [error, validate],
  );

  const onRemove = useCallback((id: string) => {
    setWorkspaceState((prevState) => prevState?.filter((item) => item !== id));
  }, []);

  const onModalSubmit = useCallback(async () => {
    if (!validate(workspaceState)) {
      return;
    }
    setSubmitting(true);

    const done = await onSubmit(workspaceState);
    if (done) {
      onClose();
    }
    setSubmitting(false);
  }, [onClose, onSubmit, validate, workspaceState]);

  const NoWorkspaceAddedYet = (
    <EmptyState
      className="w-full flex-1"
      description={t('Workspace:Modal.EmptyStateDescription')}
      title={t('Workspace:Modal.EmptyStateTitle')}
    />
  );

  return (
    <Modal
      action={{
        text: t('Common:Actions.Save'),
        onSubmit: onModalSubmit,
        discardText: t('Common:Cancel'),
        isSubmitting,
        disabled: disabled,
        ...action,
      }}
      header={{
        title: t('Common:Form.Workspaces'),
        description: t('Workspace:Description', { contentName }),
        ...header,
      }}
      variant={ModalVariant.FullHeight}
      onClose={onClose}>
      <Box className="flex flex-1 flex-col px-28 pb-128">
        <Suspense fallback={<Loader />}>
          <Box className="flex flex-1 flex-col flex-wrap">
            <AddWorkspaceSettings
              EmptyState={NoWorkspaceAddedYet}
              selectedValues={workspaceState}
              onChange={onWorkspaceChange}
              onRemove={onRemove}
            />

            {error ? (
              <Typography className="mt-8 ps-4" color="text-text-error" variant="body-short-03">
                {error}
              </Typography>
            ) : null}
          </Box>
        </Suspense>
      </Box>
    </Modal>
  );
}

export default React.memo(AddWorkspaceSettingsModal);
